import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { showNotification } from 'components';
import { NotificationType, PermissionGroupDetailModel } from 'models';
import { isAxiosError } from 'utils/errors';
import { useNotificationCatalogue, useUpdatePermissionGroup } from 'features/permissionGroups';

import { mapScopesToInput } from './PermissionGroupHeader';

enum PermissionGroupNotificationErrors {
  UNKNOWN_NOTIFICATION_TYPE = 'PERMISSION_GROUP_UNKNOWN_NOTIFICATION_TYPE',
  LOCKOUT = 'PERMISSION_GROUP_LOCKOUT',
}

const ERROR_MESSAGE_KEYS: Record<string, string> = {
  [PermissionGroupNotificationErrors.UNKNOWN_NOTIFICATION_TYPE]: `ERRORS.${PermissionGroupNotificationErrors.UNKNOWN_NOTIFICATION_TYPE}`,
  [PermissionGroupNotificationErrors.LOCKOUT]: `ERRORS.${PermissionGroupNotificationErrors.LOCKOUT}`,
};

const buildInitialSelection = (notifications: PermissionGroupDetailModel['notifications']): Set<string> =>
  new Set(notifications.map((notification) => notification.notificationType));

/**
 * Owns the notification-grant editing state for a permission group - which of the catalogued
 * notification types (e.g. SM_ORDER_DELETE) this group's members should receive, independent
 * of what scopes the group holds. Mirrors usePermissionGroupScopes, but the state itself is a
 * flat set of codes rather than a per-code view/edit grant. Edited inside a dialog opened from
 * the header, so `resetSelection` lets the dialog discard unsaved edits on Cancel/close.
 */
export const usePermissionGroupNotifications = (group: PermissionGroupDetailModel) => {
  const { t } = useTranslation();
  const { data: catalogue } = useNotificationCatalogue();
  const { mutateAsync: updatePermissionGroup, isPending } = useUpdatePermissionGroup();

  const notificationCatalogue = useMemo(() => catalogue ?? [], [catalogue]);

  const initialSelection = useMemo(() => buildInitialSelection(group.notifications), [group.notifications]);

  const [selected, setSelected] = useState<Set<string>>(initialSelection);
  const [initialSelectionSnapshot, setInitialSelectionSnapshot] = useState<Set<string>>(initialSelection);

  // Re-seed local state whenever the underlying data changes (e.g. after a save invalidation).
  const notificationsKey = group.notifications
    .map((notification) => notification.notificationType)
    .sort()
    .join(',');
  const [lastSyncKey, setLastSyncKey] = useState(notificationsKey);
  if (lastSyncKey !== notificationsKey) {
    setSelected(initialSelection);
    setInitialSelectionSnapshot(initialSelection);
    setLastSyncKey(notificationsKey);
  }

  const handleToggle = (code: string, checked: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (checked) {
        next.add(code);
      } else {
        next.delete(code);
      }
      return next;
    });
  };

  const resetSelection = () => {
    setSelected(initialSelectionSnapshot);
  };

  const isDirty = useMemo(() => {
    if (selected.size !== initialSelectionSnapshot.size) {
      return true;
    }
    return Array.from(selected).some((code) => !initialSelectionSnapshot.has(code));
  }, [selected, initialSelectionSnapshot]);

  const handleSave = async () => {
    try {
      await updatePermissionGroup({
        id: group.id,
        data: {
          name: group.name,
          isActive: group.isActive,
          scopes: mapScopesToInput(group),
          notifications: Array.from(selected).map((type) => ({ type })),
        },
      });
      setInitialSelectionSnapshot(selected);
    } catch (error) {
      if (isAxiosError(error)) {
        const code = error.response?.data?.message;
        const key = code ? ERROR_MESSAGE_KEYS[code] : undefined;
        if (key) {
          showNotification({ content: t(key), type: NotificationType.ERROR });
          return;
        }
      }
      throw error;
    }
  };

  return { notificationCatalogue, selected, handleToggle, resetSelection, isDirty, isPending, handleSave };
};
